export interface IntegrationBenefit {
  title: string;
  description: string;
  points?: string[];
}

export interface IntegrationStep {
  step: number;
  title: string;
  description: string;
}

export interface IntegrationFaq {
  q: string;
  a: string;
  enQ: string;
  enA: string;
}

export interface IntegrationDetail {
  id: string;
  slug: string;
  englishSlug: string;
  name: string;
  englishName: string;
  category: string;
  englishCategory: string;
  tagline: string;
  englishTagline: string;
  shortDescription: string;
  englishShortDescription: string;
  heroHeadline: string;
  enHeroHeadline: string;
  heroSubheadline: string;
  enHeroSubheadline: string;
  heroImage: string;
  heroImageAlt: string;
  englishHeroImageAlt: string;
  badgeText: string;
  englishBadgeText: string;
  aboutTitle: string;
  englishAboutTitle: string;
  aboutParagraphs: string[];
  englishAboutParagraphs: string[];
  benefitsTitle: string;
  englishBenefitsTitle: string;
  benefits: IntegrationBenefit[];
  englishBenefits: IntegrationBenefit[];
  setupTitle: string;
  englishSetupTitle: string;
  setupSteps: IntegrationStep[];
  englishSetupSteps: IntegrationStep[];
  technicalSpecs?: {
    label: string;
    value: string;
    enLabel: string;
    enValue: string;
  }[];
  faqs: IntegrationFaq[];
  seoTitle: string;
  englishSeoTitle: string;
  seoDescription: string;
  englishSeoDescription: string;
  iconType: 'google-drive' | 'dropbox' | 'slack' | 'sftp' | 'webhook' | 'pipedrive';
}

export const INTEGRATIONS_DATA: IntegrationDetail[] = [
  // -------------------------------------------------------------
  // 1. GOOGLE DRIVE
  // -------------------------------------------------------------
  {
    id: 'google-drive',
    slug: 'google-drive',
    englishSlug: 'google-drive',
    name: 'Google Drive',
    englishName: 'Google Drive',
    category: 'Sagsbehandling og arkivering',
    englishCategory: 'Case management & archiving',
    tagline: 'Sikker distribution og automatisk arkivering af vigtige filer',
    englishTagline: 'Secure distribution and automated archiving of vital documents',
    shortDescription: 'Opret forbindelse til Google Drev og få KvikSign til at gemme alle underskrevne dokumenter i jeres valgte mapper automatisk uden manuelt arbejde.',
    englishShortDescription: 'Connect to Google Drive and have KvikSign automatically archive all signed documents directly into your chosen folders with zero manual effort.',
    heroHeadline: 'Automatisk arkivering af underskrevne dokumenter i Google Drev',
    enHeroHeadline: 'Automated archiving of signed documents directly into Google Drive',
    heroSubheadline: 'Opret integration til Google Drev og lad KvikSign arkivere alle færdigsignerede aftaler med revisionsspor i skyen. Derved sparer du tid, fjerner fejl og bevarer det fulde overblik.',
    enHeroSubheadline: 'Set up the Google Drive integration and let KvikSign automatically store executed agreements with full audit trails in the cloud, saving time and keeping your business organized.',
    heroImage: 'https://images.unsplash.com/photo-1544396821-4dd40b938ad3?auto=format&fit=crop&w=1200&q=80',
    heroImageAlt: 'Google Drive integration til automatisk arkivering i KvikSign',
    englishHeroImageAlt: 'Google Drive integration for automated document archiving in KvikSign',
    badgeText: 'Cloud Lagring & Arkiv',
    englishBadgeText: 'Cloud Storage & Archiving',
    aboutTitle: 'Om Google Drive integrationen',
    englishAboutTitle: 'About the Google Drive integration',
    aboutParagraphs: [
      'Google Drive er verdens mest udbredte cloud-baserede lagrings- og samarbejdstjeneste, der benyttes af millioner af virksomheder til at gemme, strukturere og dele forretningskritiske filer online.',
      'Tjenesten anvender stærk kryptering både under transmissionen (TLS/SSL) og ved hvile (AES-256) på Googles sikre datacentre. Det garanterer, at virksomhedens aftaler, kontrakter og personfølsomme oplysninger altid er skærmet mod uautoriseret adgang.',
      'Med KvikSigns officielle Google Drive-integration behøver du aldrig mere downloade en underskrevet PDF for at uploade den manuelt. I det sekund alle parter har sat deres digitale signatur eller MitID-underskrift, placeres den forseglede original-PDF automatisk i jeres ønskede Google Drev-mappe.'
    ],
    englishAboutParagraphs: [
      'Google Drive is the world\'s leading cloud-based storage and workspace service, relied upon by millions of organizations to store, organize, and share mission-critical files securely.',
      'Google Drive applies high-grade encryption both in transit (TLS/SSL) and at rest (AES-256) across Google\'s state-of-the-art data centers, ensuring your contracts, agreements, and personal data remain protected against unauthorized access at all times.',
      'With KvikSign\'s native Google Drive integration, you never need to manually download and upload signed documents again. The exact second all parties sign via digital signature or MitID, the tamper-evident PDF is automatically filed into your chosen Google Drive folder.'
    ],
    benefitsTitle: 'Fordele ved en Google Drive integration i KvikSign',
    englishBenefitsTitle: 'Key benefits of integrating Google Drive with KvikSign',
    benefits: [
      {
        title: 'Sikker filoverførsel & automatisk lagring',
        description: 'Alle dokumenter, der underskrives via KvikSign, overføres krypteret og gemmes automatisk i Google Drev.',
        points: [
          'Direkte cloud-til-cloud synkronisering uden usikre e-mails',
          'Bevarer PAdES LTV-certifikatet og den uforfalskede digitale forsegling',
          'Godt egnet til store mængder af dokumenter og mange underskrivere'
        ]
      },
      {
        title: 'Centraliseret dokumenthåndtering for hele teamet',
        description: 'Saml virksomhedens vigtigste aftaler ét sted, så alle relevante medarbejdere har øjeblikkelig adgang.',
        points: [
          'Slut med at lede i personlige indbakker efter underskrevne kontrakter',
          'Understøtter både personlige drev og delte Google Workspace-drev (Shared Drives)',
          'Hurtig søgning på tværs af kunde- og leverandøraftaler'
        ]
      },
      {
        title: 'Styret adgangskontrol og GDPR-compliance',
        description: 'Brug Google Drives avancerede delingsrettigheder til at styre, hvem der kan se eller redigere følsomme dokumenter.',
        points: [
          'Finmasket adgangsstyring på brugerniveau eller afdelingsniveau',
          'Fuld overensstemmelse med GDPR og gældende databeskyttelsesregler',
          'Automatisk revisionslog og versionshistorik i Google Workspace'
        ]
      },
      {
        title: 'Spar tid og eliminer manuelt tastearbejde',
        description: 'Frigør værdifuld arbejdstid for jurister, HR-medarbejdere, revisorer og salgsteams.',
        points: [
          'Eliminerer manuelle downloads og filflytninger',
          'Reducerer risikoen for menneskelige arkiveringsfejl til et absolut nulpunkt',
          'Klar til øjeblikkelig deling med bogholder, advokat eller ledelse'
        ]
      }
    ],
    englishBenefits: [
      {
        title: 'Secure transfer & automated cloud storage',
        description: 'Every agreement completed via KvikSign is transmitted with strict encryption and filed automatically.',
        points: [
          'Direct cloud-to-cloud sync bypassing insecure email attachments',
          'Preserves PAdES LTV digital seal and tamper-evident signatures',
          'Engineered for high-volume document workflows and multi-party signing'
        ]
      },
      {
        title: 'Centralized document repository for your team',
        description: 'Consolidate all signed contracts in one secure hub accessible to authorized colleagues.',
        points: [
          'Stop searching through personal inboxes for executed contracts',
          'Works seamlessly with personal drives and Google Workspace Shared Drives',
          'Instant full-text search across client and vendor agreements'
        ]
      },
      {
        title: 'Granular access control and GDPR compliance',
        description: 'Leverage Google Drive access permissions to protect sensitive personal and commercial data.',
        points: [
          'Role-based permissions on a folder-by-folder or user basis',
          'Full compliance with GDPR and strict data protection regulations',
          'Comprehensive audit logging and version control in Google Workspace'
        ]
      },
      {
        title: 'Save hours and eliminate repetitive administrative work',
        description: 'Free your legal, HR, accounting, and sales professionals from manual file archiving.',
        points: [
          'Eliminates manual downloads and file re-uploads forever',
          'Zero risk of lost contracts or misfiled client documents',
          'Instantly available for review by auditors, managers, and external advisors'
        ]
      }
    ],
    setupTitle: 'Sådan opsætter du Google Drive i KvikSign',
    englishSetupTitle: 'How to set up Google Drive in KvikSign',
    setupSteps: [
      {
        step: 1,
        title: 'Log ind i KvikSign',
        description: 'Gå til din KvikSign-konto og åbn Virksomhedsindstillinger fra navigationsmenuen.'
      },
      {
        step: 2,
        title: 'Vælg fanen Integrationer',
        description: 'Find integrationspanelet og klik på kortet for Google Drive.'
      },
      {
        step: 3,
        title: 'Forbind din Google-konto',
        description: 'Giv KvikSign sikker tilladelse via Google OAuth-dialogen. Du bevarer den fulde kontrol over tilladelserne.'
      },
      {
        step: 4,
        title: 'Vælg destinationsmappe',
        description: 'Vælg den specifikke mappe i Google Drev, hvor underskrevne filer automatisk skal gemmes.'
      }
    ],
    englishSetupSteps: [
      {
        step: 1,
        title: 'Log in to KvikSign',
        description: 'Navigate to your KvikSign account and open Company Settings from the main navigation.'
      },
      {
        step: 2,
        title: 'Select the Integrations tab',
        description: 'Locate the integrations section and click the Google Drive card.'
      },
      {
        step: 3,
        title: 'Authorize your Google account',
        description: 'Authenticate securely via the standard Google OAuth popup window with full permission controls.'
      },
      {
        step: 4,
        title: 'Choose destination folder',
        description: 'Select the target folder or Shared Drive where executed agreements should be saved.'
      }
    ],
    technicalSpecs: [
      { label: 'Protokol', value: 'Google Drive REST API v3 med OAuth 2.0', enLabel: 'Protocol', enValue: 'Google Drive REST API v3 with OAuth 2.0' },
      { label: 'Kryptering', value: 'TLS 1.3 under overførsel, AES-256 ved hvile', enLabel: 'Encryption', enValue: 'TLS 1.3 in transit, AES-256 at rest' },
      { label: 'Filformat', value: 'PAdES LTV-forseglede PDF-dokumenter med revisionsspor', enLabel: 'File format', enValue: 'PAdES LTV-sealed PDF documents with audit trail' },
      { label: 'Drev-typer', value: 'My Drive samt Google Workspace Shared Drives', enLabel: 'Supported drives', enValue: 'My Drive & Google Workspace Shared Drives' }
    ],
    faqs: [
      {
        q: 'Overskriver KvikSign eksisterende filer i Google Drev?',
        a: 'Nej. Hvert underskrevet dokument gemmes som en unik PDF-fil med dokumentets titel, transaktions-ID og tidsstempel, så ingen tidligere filer nogensinde bliver overskrevet.',
        enQ: 'Does KvikSign overwrite existing files in Google Drive?',
        enA: 'No. Each executed contract is saved as a distinct PDF file containing the document title, transaction ID, and timestamp, guaranteeing that no prior files are ever overwritten.'
      },
      {
        q: 'Gemmes revisionssporet og certifikaterne også i Google Drive?',
        a: 'Ja. Den genererede PDF er komplet juridisk forseglet (PAdES) og indeholder samtlige underskrifter, tidsstempler, MitID-verifikationer og den officielle revisionslog.',
        enQ: 'Is the audit trail and digital certificate preserved in Google Drive?',
        enA: 'Yes. The delivered PDF is legally sealed (PAdES) and contains all digital signatures, timestamps, MitID verifications, and the complete official audit log.'
      },
      {
        q: 'Kan vi skifte mappe efter opsætningen?',
        a: 'Ja, du kan til enhver tid gå ind under Indstillinger > Integrationer og vælge en anden destinationsmappe eller oprette en ny direkte fra KvikSign.',
        enQ: 'Can we change the target folder after setup?',
        enA: 'Yes, you can visit Settings > Integrations at any time to switch destination folders or create a new folder directly from KvikSign.'
      }
    ],
    seoTitle: 'Google Drive Integration – Automatisk Arkivering | KvikSign',
    englishSeoTitle: 'Google Drive Integration – Automated Archiving | KvikSign',
    seoDescription: 'Arkiver automatisk alle underskrevne dokumenter og aftaler direkte i jeres Google Drev med KvikSign. Sikker, krypteret og GDPR-kompatibel synkronisering.',
    englishSeoDescription: 'Automatically archive all signed agreements directly into Google Drive with KvikSign. Secure, encrypted, and GDPR-compliant cloud synchronization.',
    iconType: 'google-drive'
  },

  // -------------------------------------------------------------
  // 2. SLACK
  // -------------------------------------------------------------
  {
    id: 'slack',
    slug: 'slack',
    englishSlug: 'slack',
    name: 'Slack',
    englishName: 'Slack',
    category: 'Realtidsnotifikationer og samarbejde',
    englishCategory: 'Real-time notifications & collaboration',
    tagline: 'Lynhurtige opdateringer i dine Slack-kanaler, når aftaler underskrives',
    englishTagline: 'Instant Slack channel alerts whenever agreements are executed or signed',
    shortDescription: 'Hold salgsteamet, HR og ledelsen opdateret i realtid. Modtag velformaterede Slack-beskeder med direkte links, så snart en kunde underskriver.',
    englishShortDescription: 'Keep sales, HR, and management synchronized in real time. Receive beautifully formatted Slack messages with interactive links the moment contracts are executed.',
    heroHeadline: 'Få besked i Slack i det sekund, et dokument underskrives',
    enHeroHeadline: 'Get notified in Slack the exact second an agreement is signed',
    heroSubheadline: 'Forbind KvikSign til jeres Slack-workspace og modtag direkte notifikationer i dedikerede kanaler. Følg aftalernes fremdrift, fejr nye salg og reager omgående på afvisninger.',
    enHeroSubheadline: 'Connect KvikSign to your Slack workspace and receive live alerts in dedicated channels. Track signing progress, celebrate closed deals, and address rejections instantly.',
    heroImage: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=80',
    heroImageAlt: 'Slack integration til KvikSign notifikationer og teamsamarbejde',
    englishHeroImageAlt: 'Slack integration for KvikSign team notifications and signing alerts',
    badgeText: 'Team Notifikationer & Salg',
    englishBadgeText: 'Team Notifications & Sales Alerts',
    aboutTitle: 'Om Slack integrationen',
    englishAboutTitle: 'About the Slack integration',
    aboutParagraphs: [
      'Slack er den foretrukne kommunikationshub for moderne virksomheder, hvor teams samles for at udveksle viden, koordinere projekter og træffe hurtige beslutninger.',
      'I en travl hverdag er e-mail-notifikationer ofte forsinkede, eller de drukner i overfyldte indbakker. KvikSigns Slack-integration bringer de vigtigste forretningsbegivenheder direkte ind i den kanal, hvor dit team allerede arbejder.',
      'Uanset om du vil have en notifikation i #salg, når en stor kontrakt lukkes, eller i #hr, når en ansættelseskontrakt er underskrevet, leverer KvikSign krystalklare Slack Block Kit-beskeder med oplysninger om modtager, dokumentnavn og direkte links til aftalen.'
    ],
    englishAboutParagraphs: [
      'Slack is the communication hub of modern high-performing teams, bringing people and tools together to streamline collaboration and accelerate decisions.',
      'In fast-paced environments, email alerts frequently get lost or cause inbox clutter. KvikSign\'s Slack integration delivers real-time signing milestones straight into the channels your team actively monitors.',
      'Whether notifying #sales when a major enterprise deal closes or alerting #hr when a new candidate signs an offer letter, KvikSign posts clean, structured Slack Block Kit messages with signer details, document names, and one-click action buttons.'
    ],
    benefitsTitle: 'Fordele ved en Slack integration i KvikSign',
    englishBenefitsTitle: 'Key benefits of integrating Slack with KvikSign',
    benefits: [
      {
        title: 'Øjeblikkelig reaktion og kortere salgscyklus',
        description: 'Vær den første til at vide, hvornår en kunde har sat sin underskrift, så du kan gå i gang med onboarding med det samme.',
        points: [
          'Ingen forsinkelser på grund af oversete e-mails',
          'Hurtig opfølgning giver et professionelt indtryk over for kunderne',
          'Perfekt til hurtigt voksende salgs- og konsulentvirksomheder'
        ]
      },
      {
        title: 'Gennemsigtighed for hele organisationen',
        description: 'Skab fælles synlighed over vigtige aftaler uden behov for manuelle opdateringer eller interne statusmøder.',
        points: [
          'Fejr lukkede aftaler sammen i salgskanalen og boost motivationen',
          'Giv ledelse og projektledere overblik i realtid',
          'Separate kanaler til forskellige afdelinger (f.eks. #salg, #hr, #ledelse)'
        ]
      },
      {
        title: 'Interaktive links og knapper i Slack',
        description: 'Hver besked kan indeholde en direkte handlingsknap, så autoriserede brugere kan åbne dokumentet med ét klik.',
        points: [
          'Direkte link til dokumentets status og historik i KvikSign',
          'Kompakt visning med underskriverens navn, e-mail og tidsstempel',
          'Bygget på officiel Slack Block Kit for optimal mobil- og desktopvisning'
        ]
      },
      {
        title: 'Vælg præcis hvilke hændelser der skal trigges',
        description: 'Tilpas notifikationerne så du kun modtager de beskeder, der er relevante for kanalen.',
        points: [
          'Dokument fuldført (alle parter har underskrevet)',
          'Enkelt modtager underskrev (løbende delvis fremdrift)',
          'Modtager afviste dokument (så du kan agere med det samme)',
          'Dokument afsendt eller annulleret'
        ]
      }
    ],
    englishBenefits: [
      {
        title: 'Immediate responsiveness & accelerated deal velocity',
        description: 'Be the first to know when a client signs, allowing instant handoff to implementation or customer success.',
        points: [
          'No delays caused by overlooked inbox notifications',
          'Prompt response creates an outstanding client onboarding experience',
          'Ideal for agile sales organizations and modern consultancies'
        ]
      },
      {
        title: 'Cross-functional transparency without meetings',
        description: 'Deliver shared clarity across the company without manual status messages or checking dashboards.',
        points: [
          'Celebrate won deals collectively in #sales to boost team momentum',
          'Keep executives and project managers aligned in real time',
          'Route distinct document types to dedicated channels (#sales, #hr, #legal)'
        ]
      },
      {
        title: 'Interactive buttons and action links inside Slack',
        description: 'Every alert includes structured buttons enabling authorized users to open documents in one click.',
        points: [
          'Direct link to review the transaction in the KvikSign portal',
          'High-density layout showing signer name, email, and exact timestamp',
          'Crafted with Slack Block Kit for optimal desktop and mobile rendering'
        ]
      },
      {
        title: 'Granular event routing and notification control',
        description: 'Configure event triggers so your channels receive only the signals that matter.',
        points: [
          'Transaction completed (all signers executed the document)',
          'Individual recipient signed (progress tracking)',
          'Recipient rejected (instant alert for rapid follow-up)',
          'Transaction dispatched or voided'
        ]
      }
    ],
    setupTitle: 'Sådan tilkobler du Slack i KvikSign',
    englishSetupTitle: 'How to connect Slack in KvikSign',
    setupSteps: [
      {
        step: 1,
        title: 'Gå til Indstillinger > Integrationer',
        description: 'Log ind på din KvikSign-konto og åbn integrationsfanen.'
      },
      {
        step: 2,
        title: 'Klik på Forbind til Slack',
        description: 'Klik på knappen for at starte den sikre godkendelse i et sikkert popup-vindue.'
      },
      {
        step: 3,
        title: 'Vælg Slack-kanal',
        description: 'I Slacks godkendelsesdialog vælger du direkte, hvilken kanal (f.eks. #underskrifter eller #salg) der skal modtage beskederne.'
      },
      {
        step: 4,
        title: 'Vælg hændelser og test',
        description: 'Vælg hvilke hændelser der skal trigge beskeder, og afsend en hurtig testbesked direkte fra KvikSign.'
      }
    ],
    englishSetupSteps: [
      {
        step: 1,
        title: 'Go to Settings > Integrations',
        description: 'Log in to your KvikSign workspace and navigate to the Integrations panel.'
      },
      {
        step: 2,
        title: 'Click Connect to Slack',
        description: 'Trigger the official OAuth authorization flow in a secure popup window.'
      },
      {
        step: 3,
        title: 'Select target Slack channel',
        description: 'Choose the public or private channel (e.g. #sales or #contracts) inside your Slack workspace.'
      },
      {
        step: 4,
        title: 'Configure events & verify',
        description: 'Select your preferred triggers and send an instant live test notification.'
      }
    ],
    technicalSpecs: [
      { label: 'Protokol', value: 'Slack OAuth 2.0 Webhook API', enLabel: 'Protocol', enValue: 'Slack OAuth 2.0 Webhook API' },
      { label: 'Formatering', value: 'Slack Block Kit med interaktive knapper', enLabel: 'Formatting', enValue: 'Slack Block Kit with interactive buttons' },
      { label: 'Sikkerhed', value: 'OAuth 2.0 token baseret autorisation', enLabel: 'Security', enValue: 'OAuth 2.0 token-based authorization' },
      { label: 'Hændelser', value: 'Completed, Signed, Rejected, Cancelled, Created', enLabel: 'Events', enValue: 'Completed, Signed, Rejected, Cancelled, Created' }
    ],
    faqs: [
      {
        q: 'Kan vi forbinde flere forskellige Slack-kanaler?',
        a: 'Ja, du kan oprette flere Slack-integrationer i KvikSign og dirigere forskellige hændelser til forskellige kanaler – f.eks. ansættelser til #hr og kundekontrakter til #salg.',
        enQ: 'Can we connect multiple different Slack channels?',
        enA: 'Yes, you can configure multiple Slack integrations in KvikSign and route different agreements to separate channels – such as employment agreements to #hr and sales deals to #sales.'
      },
      {
        q: 'Er selve PDF-dokumentet synligt for alle i Slack?',
        a: 'Nej. Af hensyn til sikkerhed og GDPR sender KvikSign et link til dokumentet i KvikSign, hvor brugeren skal være autoriseret for at åbne eller hente filen.',
        enQ: 'Is the actual PDF file publicly visible to everyone in the Slack channel?',
        enA: 'No. To ensure security and strict GDPR compliance, KvikSign provides a secure direct link requiring authorized credentials to view or download the file.'
      },
      {
        q: 'Virker integrationen både med gratis og betalte Slack-workspaces?',
        a: 'Ja, KvikSigns Slack-integration er 100% kompatibel med alle typer Slack-workspaces (Free, Pro, Business+ og Enterprise Grid).',
        enQ: 'Does the integration work with both free and paid Slack workspaces?',
        enA: 'Yes, KvikSign\'s Slack integration is fully compatible with all Slack workspace tiers (Free, Pro, Business+, and Enterprise Grid).'
      }
    ],
    seoTitle: 'Slack Integration – Realtidsnotifikationer | KvikSign',
    englishSeoTitle: 'Slack Integration – Real-Time Signing Alerts | KvikSign',
    seoDescription: 'Få direkte besked i Slack, når dine kunder eller medarbejdere underskriver dokumenter i KvikSign. Hold salg, HR og ledelse opdateret i realtid.',
    englishSeoDescription: 'Get real-time Slack notifications when contracts and documents are signed in KvikSign. Keep sales, HR, and executives aligned instantly.',
    iconType: 'slack'
  },

  // -------------------------------------------------------------
  // 3. SFTP (SECURE FILE TRANSFER PROTOCOL)
  // -------------------------------------------------------------
  {
    id: 'sftp',
    slug: 'sftp',
    englishSlug: 'sftp',
    name: 'Sikker FTP (SFTP)',
    englishName: 'Secure FTP (SFTP)',
    category: 'Sagsbehandling og dokumenthåndtering',
    englishCategory: 'Case management & document delivery',
    tagline: 'Sikker distribution af vigtige filer direkte til jeres egne servere',
    englishTagline: 'Secure automated distribution of signed files directly to your internal servers',
    shortDescription: 'Secure File Transfer Protocol gør det muligt at overføre underskrevne dokumenter automatisk til jeres egne servere, firewalls og lukkede on-premise arkiver via krypteret SSH.',
    englishShortDescription: 'Secure File Transfer Protocol enables automated transfer of executed contracts directly into your private servers, firewalls, and on-premise archives via encrypted SSH.',
    heroHeadline: 'Overfør underskrevne dokumenter sikkert til egne servere med SFTP',
    enHeroHeadline: 'Transmit signed documents securely to your private servers via SFTP',
    heroSubheadline: 'Secure File Transfer Protocol gør det muligt at sende underskrevne dokumenter direkte til jeres egne servere, on-premise arkiver og opbygge unikke forretningsflows uden tredjepartsløsninger.',
    enHeroSubheadline: 'Secure File Transfer Protocol allows you to transfer executed agreements directly to your self-hosted servers and build automated workflows without relying on third-party cloud drives.',
    heroImage: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1200&q=80',
    heroImageAlt: 'SFTP serverintegration til sikker filoverførsel i KvikSign',
    englishHeroImageAlt: 'SFTP server integration for secure file transfers in KvikSign',
    badgeText: 'Enterprise & On-Premise',
    englishBadgeText: 'Enterprise & On-Premise',
    aboutTitle: 'Om Secure File Transfer Protocol (SFTP)',
    englishAboutTitle: 'About Secure File Transfer Protocol (SFTP)',
    aboutParagraphs: [
      'SFTP står for SSH File Transfer Protocol eller Secure File Transfer Protocol. Det er den absolutte industristandard for sikker overførsel af filer mellem servere via en stærkt krypteret forbindelse, som bygger på SSH (Secure Shell) protokollen.',
      'I modsætning til ældre protokoller som almindelig FTP krypterer SFTP både selve filindholdet, overførselskommandoerne og autentifikationen (login-oplysninger eller SSH-nøgler). Dette eliminerer risikoen for, at uvedkommende kan opsnappe, lytte med på eller manipulere data under transmissionen.',
      'Med KvikSigns indbyggede SFTP-integration kan din virksomhed opnå 100% automatiseret distribution af færdigsignerede dokumenter direkte ind i jeres egne dokumentarkiver, ERP-systemer, journaler eller servermiljøer bag jeres egen firewall.'
    ],
    englishAboutParagraphs: [
      'SFTP stands for SSH File Transfer Protocol (or Secure File Transfer Protocol). It is the recognized enterprise benchmark for transferring confidential files between remote systems over an encrypted SSH connection.',
      'Unlike legacy FTP, SFTP encrypts the full payload, control commands, and authentication credentials (whether passwords or cryptographic SSH keypairs). This guarantees that sensitive corporate data and identity records cannot be intercepted or manipulated during transit.',
      'With KvikSign\'s native SFTP integration, your organization achieves 100% automated delivery of legally executed agreements straight into your private on-premise file stores, ERP databases, or document archives behind your enterprise firewall.'
    ],
    benefitsTitle: 'Fordele ved en SFTP integration i KvikSign',
    englishBenefitsTitle: 'Key benefits of integrating SFTP with KvikSign',
    benefits: [
      {
        title: 'Sikker filoverførsel og dataejerskab',
        description: 'Bevar fuld kontrol over hvor jeres underskrevne dokumenter opbevares, uden at være bundet til eksterne cloud-udbydere.',
        points: [
          'Dokumenter overføres direkte til jeres egne servere via krypteret SSH',
          'I bestemmer selv adgangsrettigheder, filstruktur og backup-politikker',
          'Optimalt til håndtering af store mængder dokumenter og tunge filer'
        ]
      },
      {
        title: 'Fuld automatisering af dokumentflowet',
        description: 'Slut med at logge ind i flere systemer eller downloade filer manuelt – hele processen foregår automatisk.',
        points: [
          'Automatisk upload i det øjeblik alle parter har underskrevet',
          'Automatisk oprettelse af undermapper baseret på fagsystemets krav',
          'Ingen manuelle mellemled, der kan forsinke eller fejlplacere filer'
        ]
      },
      {
        title: 'Maksimal datasikkerhed og integritet',
        description: 'Følsomme kontrakter, personoplysninger og CPR-data beskyttes mod uautoriseret adgang.',
        points: [
          'Understøtter stærk asymmetrisk SSH privat nøgle autentifikation (samt adgangskode)',
          'End-to-end kryptering under transmissionen sikrer 100% integritet',
          'Bevarer PAdES LTV-certifikater, digitale tidsstempler og fuldt revisionsspor'
        ]
      },
      {
        title: 'Detaljeret logning, sporbarhed og compliance',
        description: 'Fuld dokumentation for hvem der har overført hvilke filer og hvornår, i overensstemmelse med GDPR og revisionskrav.',
        points: [
          'Indbygget forbindelsestest og automatisk fejllogning i KvikSign',
          'Opfylder de strengeste krav inden for finans, forsikring og offentlig forvaltning',
          'Robust arkitektur med mulighed for tilpassede destinationsstier'
        ]
      }
    ],
    englishBenefits: [
      {
        title: 'Secure file delivery & complete data sovereignty',
        description: 'Maintain absolute authority over where your signed contracts reside without reliance on third-party public clouds.',
        points: [
          'Agreements transferred straight to your servers over high-grade encrypted SSH',
          'Full control over internal permissions, directory structures, and backup schedules',
          'Engineered to handle high transaction volumes and large document packages'
        ]
      },
      {
        title: 'End-to-end workflow automation',
        description: 'No more switching between systems or manual file handling – the entire pipeline runs hands-free.',
        points: [
          'Instant upload triggered the exact moment a document is fully executed',
          'Automatic directory creation according to your business hierarchy',
          'Eliminates human error, misplaced records, and manual processing lags'
        ]
      },
      {
        title: 'Institutional-grade data protection & integrity',
        description: 'Confidential commercial contracts and sensitive personal records remain shielded from intrusion.',
        points: [
          'Supports strong asymmetric SSH private key authentication or enterprise passwords',
          'End-to-end cryptographic transport ensures tamper prevention',
          'Preserves all PAdES LTV digital signatures, timestamps, and audit certificates'
        ]
      },
      {
        title: 'Detailed transfer logging, traceability & compliance',
        description: 'Comprehensive documentation of file movements ensuring adherence to strict internal audit and GDPR mandates.',
        points: [
          'Integrated connection diagnostic tool and status verification in KvikSign',
          'Meets regulatory compliance standards across finance, insurance, and public sectors',
          'Flexible target pathing customized to your file system schema'
        ]
      }
    ],
    setupTitle: 'Sådan opsætter du Sikker FTP i KvikSign',
    englishSetupTitle: 'How to configure Secure FTP in KvikSign',
    setupSteps: [
      {
        step: 1,
        title: 'Gå til Indstillinger > Integrationer',
        description: 'Log ind i KvikSign og vælg Sikker FTP (SFTP) under integrationsoversigten.'
      },
      {
        step: 2,
        title: 'Indtast serveroplysninger',
        description: 'Udfyld værtsnavn/IP-adresse, port (standard 22) og dit SFTP-brugernavn.'
      },
      {
        step: 3,
        title: 'Vælg autentifikationsmetode',
        description: 'Vælg adgangskode eller indsæt en sikker SSH privat nøgle (anbefalet for enterprise-sikkerhed).'
      },
      {
        step: 4,
        title: 'Test forbindelsen og gem',
        description: 'Klik på Test forbindelse for at verificere forbindelsen og destinationsstien, og aktivér integrationen.'
      }
    ],
    englishSetupSteps: [
      {
        step: 1,
        title: 'Go to Settings > Integrations',
        description: 'Log in to your KvikSign portal and select Secure FTP (SFTP) in the integrations menu.'
      },
      {
        step: 2,
        title: 'Enter server host & credentials',
        description: 'Provide your server hostname/IP, port (default 22), and SFTP username.'
      },
      {
        step: 3,
        title: 'Select authentication method',
        description: 'Choose password authentication or paste an SSH private key (recommended for maximum security).'
      },
      {
        step: 4,
        title: 'Test connection & activate',
        description: 'Click Test connection to verify server reachability and target paths, then save.'
      }
    ],
    technicalSpecs: [
      { label: 'Protokol', value: 'SSH File Transfer Protocol (SFTP v3/v4)', enLabel: 'Protocol', enValue: 'SSH File Transfer Protocol (SFTP v3/v4)' },
      { label: 'Standard Port', value: 'Port 22 (eller brugerdefineret port)', enLabel: 'Default Port', enValue: 'Port 22 (or custom port)' },
      { label: 'Autentifikation', value: 'Adgangskode eller RSA/ED25519 SSH Privat Nøgle', enLabel: 'Authentication', enValue: 'Password or RSA/ED25519 SSH Private Key' },
      { label: 'Mappestyring', value: 'Automatisk oprettelse af undermapper (auto-create)', enLabel: 'Directories', enValue: 'Automatic directory provisioning' }
    ],
    faqs: [
      {
        q: 'Hvad er forskellen på almindelig FTP og SFTP?',
        a: 'Almindelig FTP sender både login-oplysninger og dokumenter i ukrypteret klartekst. SFTP benytter SSH-kryptering, hvilket gør transmissionen 100% sikker og modstandsdygtig over for aflytning.',
        enQ: 'What is the difference between standard FTP and SFTP?',
        enA: 'Standard FTP transmits credentials and files in unencrypted plain text. SFTP leverages SSH cryptographic protocols, making all communications completely secure and tamper-proof.'
      },
      {
        q: 'Skal vores server have en fast IP-adresse?',
        a: 'Ja, vi anbefaler et fast værtsnavn eller en fast offentlig IP-adresse, så KvikSigns overførselsservice altid kan nå jeres SFTP-server pålideligt.',
        enQ: 'Does our server require a static IP address or hostname?',
        enA: 'Yes, we recommend a static hostname or fixed public IP address to ensure KvikSign\'s delivery service can consistently reach your SFTP endpoint.'
      },
      {
        q: 'Kan KvikSign automatisk oprette mapper på serveren?',
        a: 'Ja, KvikSign har en indbygget funktion til automatisk at oprette den angivne mappesti, hvis den ikke allerede eksisterer på jeres SFTP-server.',
        enQ: 'Can KvikSign automatically create missing directories on the server?',
        enA: 'Yes, KvikSign includes automated directory creation if the designated target path does not already exist on your SFTP server.'
      }
    ],
    seoTitle: 'SFTP Integration – Sikker Filoverførsel | KvikSign',
    englishSeoTitle: 'SFTP Integration – Secure File Delivery | KvikSign',
    seoDescription: 'Overfør automatisk underskrevne dokumenter til jeres egne servere med KvikSigns sikre SFTP-integration. SSH-kryptering, adgangskode- eller nøgleautentifikation.',
    englishSeoDescription: 'Automatically transfer executed contracts directly to your private servers via KvikSign\'s secure SFTP integration with SSH encryption.',
    iconType: 'sftp'
  },

  // -------------------------------------------------------------
  // 4. WEBHOOKS
  // -------------------------------------------------------------
  {
    id: 'webhooks',
    slug: 'webhooks',
    englishSlug: 'webhooks',
    name: 'Webhooks',
    englishName: 'Webhooks',
    category: 'Hændelsesstyret automatisering & API',
    englishCategory: 'Event-driven automation & API',
    tagline: 'Lynhurtig realtidssynkronisering med jeres CRM, ERP og fagsystemer',
    englishTagline: 'Lightning-fast real-time synchronization with your CRM, ERP, and internal systems',
    shortDescription: 'Forbind KvikSign direkte til jeres backend, ERP (f.eks. e-conomic, Dinero) eller CRM (HubSpot, Salesforce). Modtag hændelsesdrevne HTTP POST-kald med strukturerede JSON-data.',
    englishShortDescription: 'Connect KvikSign directly to your backend, ERP (e-conomic, Dinero, Business Central) or CRM (HubSpot, Salesforce) via event-driven HTTP POST webhooks.',
    heroHeadline: 'Automatiser jeres forretningssystemer med KvikSign Webhooks',
    enHeroHeadline: 'Automate your enterprise systems with KvikSign Webhooks',
    heroSubheadline: 'Få øjeblikkelig besked i jeres egne systemer, når en aftale oprettes, underskrives, afvises eller fuldføres. Udløs automatiske fakturaer, ordrebekræftelser eller sagsopdateringer uden forsinkelse.',
    enHeroSubheadline: 'Receive instant notifications in your proprietary software whenever a document is signed or executed. Trigger automated invoices, order confirmations, or case updates in real time.',
    heroImage: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80',
    heroImageAlt: 'Webhooks API integration i KvikSign til automatisering af forretningsgange',
    englishHeroImageAlt: 'Webhooks API integration in KvikSign for business process automation',
    badgeText: 'REST API & Hændelser',
    englishBadgeText: 'REST API & Webhooks',
    aboutTitle: 'Om KvikSign Webhooks',
    englishAboutTitle: 'About KvikSign Webhooks',
    aboutParagraphs: [
      'Et webhook er en moderne, letvægts metode til at få to systemer til at kommunikere i realtid uden behov for konstant polling. Når en hændelse sker i KvikSign, sendes en HTTP POST-anmodning med det samme til jeres specificerede endpoint.',
      'Hvert webhook-kald indeholder en komplet, struktureret JSON-payload med transaktionens ID, dokumentnavn, status, modtagerinformationer, tidsstempler og sikre download-links til det underskrevne dokument.',
      'Uanset om du bruger Zapier, Make, n8n eller jeres eget specialudviklede IT-system i Python, Node.js, C# eller PHP, kan du oprette en pålidelig integration på få minutter.'
    ],
    englishAboutParagraphs: [
      'Webhooks represent the modern industry standard for real-time, event-driven communication between web applications without tedious polling loops. When an event takes place in KvikSign, an HTTP POST request is instantly dispatched to your designated URL.',
      'Each webhook delivery includes a structured JSON payload detailing the transaction ID, document name, lifecycle status, signer information, timestamps, and secure download links for executed files.',
      'Whether you connect via Zapier, Make, n8n, or your in-house engineering stack in Node.js, Python, .NET, or Go, you can establish an enterprise integration in minutes.'
    ],
    benefitsTitle: 'Fordele ved Webhooks i KvikSign',
    englishBenefitsTitle: 'Key benefits of using Webhooks in KvikSign',
    benefits: [
      {
        title: 'Realtidssynkronisering uden forsinkelse',
        description: 'Dine systemer opdateres i det sekund, en underskrift sættes – ingen forsinkelser, ingen manuelle tjek.',
        points: [
          'Øjeblikkelig registrering af fuldførte kontrakter og ordrer',
          'Automatisk opdatering af kundestatus i CRM (f.eks. HubSpot, Salesforce, Pipedrive)',
          'Udløs automatisk oprettelse af faktura i e-conomic eller Dinero'
        ]
      },
      {
        title: 'Fuldstændig og veldokumenteret JSON-payload',
        description: 'Modtag alle nødvendige informationer i en klar og overskuelig datastruktur.',
        points: [
          'Transaktions-ID, referencenummer og virksomhedsdata',
          'Modtagerens navn, e-mail og underskriftsmetode (f.eks. MitID)',
          'Direkte sikre URLs til download af underskrevne dokumenter'
        ]
      },
      {
        title: 'Sikker autentifikation og tilpassede headere',
        description: 'Beskyt dine webhook-endepunkter mod uautoriserede anmodninger.',
        points: [
          'Understøtter Bearer Token, Basic Auth eller custom HTTP-headere',
          'Hemmelig signeringsnøgle (Secret Token) til verifikation af afsender',
          'Gennemsigtig logning af HTTP-svarkoder (f.eks. 200 OK) for fejlfinding'
        ]
      },
      {
        title: 'Fleksibelt hændelsesfilter og indbygget test',
        description: 'Vælg nøjagtig hvilke hændelser der skal udløse webhooks, og afprøv dem direkte fra interfacet.',
        points: [
          'Filtrér på `transaction.completed`, `signer.signed`, `signer.rejected`, mv.',
          'Et-klik live testfunktion der sender en realistisk test-payload',
          'Slå webhooks til og fra med et enkelt klik'
        ]
      }
    ],
    englishBenefits: [
      {
        title: 'Zero-latency real-time synchronization',
        description: 'Your databases and services update the instant a signature is applied – no delays, no manual auditing.',
        points: [
          'Instant recognition of executed agreements and client orders',
          'Automatic deal stage advancement in CRMs (HubSpot, Salesforce, Pipedrive)',
          'Trigger automated invoice generation in accounting software (e-conomic, Xero)'
        ]
      },
      {
        title: 'Comprehensive, developer-friendly JSON payloads',
        description: 'Receive all relevant metadata packaged in clean, predictable data structures.',
        points: [
          'Transaction ID, custom references, and organization identifiers',
          'Signer identity, email, and authentication method (e.g. MitID)',
          'Secure download URLs to fetch legally sealed PDF files directly'
        ]
      },
      {
        title: 'Robust authentication and custom HTTP headers',
        description: 'Secure your receiving webhook endpoints against spoofing or unauthorized traffic.',
        points: [
          'Support for Bearer Tokens, HTTP Basic Auth, or custom headers',
          'Secret token verification guaranteeing payloads originate from KvikSign',
          'Transparent status logging and HTTP response code tracking (e.g. 200 OK)'
        ]
      },
      {
        title: 'Granular event triggers and interactive testing',
        description: 'Filter specific events and verify your endpoint directly from the KvikSign interface.',
        points: [
          'Listen selectively for `transaction.completed`, `signer.signed`, `signer.rejected`',
          'One-click live test button dispatching realistic test payloads to your URL',
          'Toggle integrations active or paused instantly without deleting settings'
        ]
      }
    ],
    setupTitle: 'Sådan opsætter du Webhooks i KvikSign',
    englishSetupTitle: 'How to configure Webhooks in KvikSign',
    setupSteps: [
      {
        step: 1,
        title: 'Gå til Indstillinger > Integrationer',
        description: 'Log ind i KvikSign og klik på Webhook-kortet under integrationsoversigten.'
      },
      {
        step: 2,
        title: 'Indtast modtager-URL',
        description: 'Angiv den fulde HTTPS-adresse på din server, API-gateway eller automatiseringsservice (Zapier/Make).'
      },
      {
        step: 3,
        title: 'Vælg autentifikation og hændelser',
        description: 'Indstil eventuelt Bearer Token eller Basic Auth, og vælg hvilke hændelser der skal sendes.'
      },
      {
        step: 4,
        title: 'Send en test og aktivér',
        description: 'Klik på Send test for at verificere, at dit system modtager og svarer med 200 OK, og gem integrationen.'
      }
    ],
    englishSetupSteps: [
      {
        step: 1,
        title: 'Go to Settings > Integrations',
        description: 'Log in to KvikSign and select the Webhook card from the Integrations panel.'
      },
      {
        step: 2,
        title: 'Enter destination webhook URL',
        description: 'Specify the secure HTTPS endpoint on your backend, API gateway, or workflow platform (Zapier/Make).'
      },
      {
        step: 3,
        title: 'Set authentication & events',
        description: 'Provide an optional Bearer Token or Basic Auth header and check the event triggers you require.'
      },
      {
        step: 4,
        title: 'Send test payload & activate',
        description: 'Click Send test to confirm your endpoint responds with HTTP 200 OK, then save.'
      }
    ],
    technicalSpecs: [
      { label: 'Metode', value: 'HTTP POST med Content-Type: application/json', enLabel: 'Method', enValue: 'HTTP POST with Content-Type: application/json' },
      { label: 'Autentifikation', value: 'Bearer Token, Basic Auth eller Custom Header', enLabel: 'Auth', enValue: 'Bearer Token, Basic Auth or Custom Header' },
      { label: 'Format', value: 'JSON Schema v4 kompatibel payload', enLabel: 'Format', enValue: 'JSON Schema v4 compatible payload' },
      { label: 'Hændelser', value: 'transaction.completed, signer.signed, signer.rejected, etc.', enLabel: 'Events', enValue: 'transaction.completed, signer.signed, signer.rejected, etc.' }
    ],
    faqs: [
      {
        q: 'Hvad sker der, hvis vores server er midlertidigt nede?',
        a: 'KvikSign logger fejl og statuskoder (f.eks. 500 eller timeout). Vores system forsøger automatisk at gensende mislykkede webhooks for at sikre, at ingen data går tabt.',
        enQ: 'What happens if our server is temporarily offline?',
        enA: 'KvikSign logs all failed response codes (e.g. 500 or timeout). The service includes retry logic to ensure no critical notifications are missed.'
      },
      {
        q: 'Kan vi bruge Webhooks sammen med no-code værktøjer som Zapier eller Make?',
        a: 'Ja! Du kan indsætte en webhook-URL fra Zapier ("Catch Hook"), Make eller n8n direkte i KvikSign og bygge avancerede no-code flows på få minutter.',
        enQ: 'Can we use Webhooks with no-code platforms like Zapier or Make?',
        enA: 'Yes! Simply paste a webhook URL from Zapier ("Catch Hook"), Make, or n8n into KvikSign to build powerful automated workflows in minutes without writing code.'
      },
      {
        q: 'Kræver Webhooks en HTTPS-forbindelse?',
        a: 'Ja, af hensyn til sikkerhed og beskyttelse af forretningsdata kræver KvikSign, at alle webhook-URLs benytter krypteret HTTPS.',
        enQ: 'Do Webhooks require an HTTPS connection?',
        enA: 'Yes, to safeguard confidential transaction data and credentials, KvikSign enforces secure HTTPS for all destination endpoints.'
      }
    ],
    seoTitle: 'Webhooks Integration – Forbind til Systemer | KvikSign',
    englishSeoTitle: 'Webhooks Integration – Connect Your Systems | KvikSign',
    seoDescription: 'Få realtidsopdateringer i jeres CRM, ERP og interne systemer med KvikSigns REST Webhooks. Automatisk synkronisering af underskrevne dokumenter via JSON.',
    englishSeoDescription: 'Receive real-time notifications in your CRM, ERP, and proprietary software via KvikSign\'s REST Webhooks. Event-driven JSON synchronization.',
    iconType: 'webhook'
  },

  // -------------------------------------------------------------
  // 5. PIPEDRIVE CRM
  // -------------------------------------------------------------
  {
    id: 'pipedrive',
    slug: 'pipedrive',
    englishSlug: 'pipedrive',
    name: 'Pipedrive',
    englishName: 'Pipedrive',
    category: 'CRM, Salg & Kontraktstyring',
    englishCategory: 'CRM, Sales & Contract Management',
    tagline: 'Anmod om signering fra en Deal, få det signerede dokument retur og opdater værdier i Pipedrive',
    englishTagline: 'Request signatures from a Deal, return the signed PDF, and update Deal fields in Pipedrive',
    shortDescription: 'Forbind Pipedrive med KvikSign: Send kontrakter til underskrift fra en Deal, få det signerede og forseglede dokument automatisk retur på Dealen og sæt status til Won samt opdater Custom Fields.',
    englishShortDescription: 'Connect Pipedrive with KvikSign: Request signatures for a Deal, automatically attach the signed PDF back to the Deal, mark the Deal as Won, and update custom Deal fields.',
    heroHeadline: 'Automatiser kontraktsignering direkte i din Pipedrive salgspipeline',
    enHeroHeadline: 'Automate contract signing directly inside your Pipedrive sales pipeline',
    heroSubheadline: 'Når et dokument underskrives i KvikSign, sendes den signerede PDF automatisk retur til Dealen i Pipedrive, Deal-status sættes til Vundet (Won), og dine valgte felter og tidslinjenoter opdateres i realtid.',
    enHeroSubheadline: 'When a contract is signed in KvikSign, the sealed PDF is automatically returned to the Pipedrive Deal, the Deal is marked Won, and your mapped Deal fields and timeline notes update in real time.',
    heroImage: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=80',
    heroImageAlt: 'Pipedrive CRM integration til digital signering i KvikSign',
    englishHeroImageAlt: 'Pipedrive CRM integration for digital signing in KvikSign',
    badgeText: 'CRM & Salgsautomatisering',
    englishBadgeText: 'CRM & Sales Automation',
    aboutTitle: 'Om Pipedrive integrationen',
    englishAboutTitle: 'About the Pipedrive integration',
    aboutParagraphs: [
      'Pipedrive er et af verdens mest populære salgs-CRM-systemer, designet til at give salgsteams fuldt visuelt overblik over pipelines, leads og aktive Deals.',
      'Med KvikSigns 2-vejs Pipedrive-integration slipper sælgere for manuelt at downloade kontrakter, oprette forsendelser og uploade underskrevne PDF-filer tilbage på Dealen.',
      'Når kunden har underskrevet med MitID eller digital signatur, vedhæftes det signerede og GlobalSign-forseglede dokument automatisk under "Filer" på Dealen, Dealen markeres som Vundet (eller flyttes til næste stadie), og relevante felter samt tidslinjenoter opdateres helt automatisk.'
    ],
    englishAboutParagraphs: [
      'Pipedrive is one of the world\'s leading sales CRM platforms, built to give revenue teams complete visibility across pipelines, contacts, and active Deals.',
      'With KvikSign\'s bidirectional Pipedrive integration, sales reps no longer need to manually download contracts, send them out, and re-upload executed PDFs back into Pipedrive.',
      'The moment a client signs via MitID or digital signature, the GlobalSign-sealed PDF is automatically attached under "Files" on the Deal, the Deal status advances to Won, and custom Deal fields and timeline notes are updated automatically.'
    ],
    benefitsTitle: 'Fordele ved Pipedrive integration i KvikSign',
    englishBenefitsTitle: 'Key benefits of integrating Pipedrive with KvikSign',
    benefits: [
      {
        title: 'Signeret PDF sendes automatisk retur til Dealen',
        description: 'Det færdigsignerede dokument lander automatisk præcis dér, hvor det kom fra i Pipedrive.',
        points: [
          'Vedhæftes automatisk under "Filer" på selve Dealen, Kontaktpersonen og Organisationen',
          'Indeholder fuldt revisionsspor, MitID-verifikation og PAdES LTV-forsegling',
          'Altid tilgængeligt for sælger, onboarding-team og bogholderi direkte i Pipedrive'
        ]
      },
      {
        title: 'Automatisk opdatering af Deal-status, Stadie og Custom Fields',
        description: 'Lad signeringen opdatere værdier og rykke Dealen videre i din pipeline uden manuelt arbejde.',
        points: [
          'Sæt automatisk Deal-status til Vundet (Won) ved underskrift eller Tabt (Lost) ved afvisning',
          'Flyt automatisk Dealen til et bestemt Pipeline-stadie ved afsendelse og ved færdigsignering',
          'Map KvikSign-værdier (underskriftsdato, MitID-navn, metode, transaktions-ID) direkte ind i Pipedrive Custom Fields'
        ]
      },
      {
        title: 'Anmod om signering direkte fra Pipedrive',
        description: 'Start signeringsflowet så snart en Deal flyttes til dit valgte kontrakt-stadie i Pipedrive.',
        points: [
          'Henter automatisk Dealens tilknyttede kontaktperson (navn, e-mail og telefon)',
          'Henter vedhæftede PDF/Word-dokumenter direkte fra Dealen i Pipedrive',
          'Automatisk match på Deal-ID i referencenummer eller via underskrivers e-mail'
        ]
      },
      {
        title: 'Tidslinje-noter og automatiske opfølgningsaktiviteter',
        description: 'Hold hele salgsteamet opdateret direkte på Dealens tidslinje.',
        points: [
          'Automatisk Note på Dealen når dokumentet sendes, underskrives eller afvises',
          'Mulighed for automatisk at oprette en opfølgnings- eller onboarding-aktivitet i Pipedrive'
        ]
      }
    ],
    englishBenefits: [
      {
        title: 'Signed PDF automatically returned to the Deal',
        description: 'The executed contract is automatically attached right back where it originated in Pipedrive.',
        points: [
          'Attached under "Files" on the Deal, Person, and Organization',
          'Includes full audit log, MitID verification, and PAdES LTV digital seal',
          'Immediately accessible to sales, onboarding, and finance inside Pipedrive'
        ]
      },
      {
        title: 'Automatic Deal Status, Stage & Custom Field updates',
        description: 'Let completed signatures update Deal values and advance your pipeline hands-free.',
        points: [
          'Automatically mark Deals as Won upon completion or Lost upon rejection',
          'Advance Deals to specific Pipeline Stages when sent and when signed',
          'Map KvikSign values (signed timestamp, signer name, method, transaction ID) to Pipedrive Custom Fields'
        ]
      },
      {
        title: 'Request signatures directly from Pipedrive',
        description: 'Trigger signature workflows when a Deal reaches your designated contract stage.',
        points: [
          'Pulls the Deal\'s primary contact person (name, email, phone)',
          'Fetches attached PDF/DOCX agreements directly from the Pipedrive Deal',
          'Auto-matches Deals via Reference Number (Deal #ID) or recipient email'
        ]
      },
      {
        title: 'Timeline Audit Notes & Follow-up Activities',
        description: 'Keep the entire revenue team informed right on the Deal timeline.',
        points: [
          'Automatic timeline notes when contracts are dispatched, signed, or rejected',
          'Optional automatic creation of onboarding or follow-up tasks in Pipedrive'
        ]
      }
    ],
    setupTitle: 'Sådan opsætter du Pipedrive i KvikSign',
    englishSetupTitle: 'How to set up Pipedrive in KvikSign',
    setupSteps: [
      {
        step: 1,
        title: 'Gå til Indstillinger > Integrationer',
        description: 'Log ind i KvikSign og klik på Pipedrive-kortet under integrationsoversigten.'
      },
      {
        step: 2,
        title: 'Forbind via OAuth 2.0 eller API-nøgle',
        description: 'Forbind din Pipedrive-konto med OAuth (App name: KvikSign) eller indsæt din personlige Pipedrive API-nøgle.'
      },
      {
        step: 3,
        title: 'Vælg retur-flow og felt-opdateringer',
        description: 'Aktivér automatisk upload af signeret PDF til Dealen, vælg Deal-status (f.eks. Won) og map eventuelle Custom Fields.'
      },
      {
        step: 4,
        title: 'Test forbindelsen og gem',
        description: 'Klik på Test forbindelse for at bekræfte adgangen til Pipedrive, og gem opsætningen.'
      }
    ],
    englishSetupSteps: [
      {
        step: 1,
        title: 'Go to Settings > Integrations',
        description: 'Log in to KvikSign and click the Pipedrive card in the Integrations panel.'
      },
      {
        step: 2,
        title: 'Connect via OAuth 2.0 or API Token',
        description: 'Authorize your Pipedrive account via OAuth (App name: KvikSign) or enter your Pipedrive API Token.'
      },
      {
        step: 3,
        title: 'Configure return PDF & field mappings',
        description: 'Enable automatic upload of signed PDFs back to the Deal, select Deal status/stage updates, and map Custom Fields.'
      },
      {
        step: 4,
        title: 'Test connection & save',
        description: 'Click Test connection to verify your Pipedrive account and save your configuration.'
      }
    ],
    technicalSpecs: [
      { label: 'Protokol', value: 'Pipedrive REST API v1 med OAuth 2.0 & API Token', enLabel: 'Protocol', enValue: 'Pipedrive REST API v1 with OAuth 2.0 & API Token' },
      { label: 'App Name', value: 'KvikSign', enLabel: 'App Name', enValue: 'KvikSign' },
      { label: 'Callback URL', value: 'https://kviksign.com/api/integrations/pipedrive/callback', enLabel: 'Callback URL', enValue: 'https://kviksign.com/api/integrations/pipedrive/callback' },
      { label: 'Synkronisering', value: '2-vejs (Deals, Files, Notes, Stages, Custom Fields & Activities)', enLabel: 'Sync', enValue: 'Bidirectional (Deals, Files, Notes, Stages, Custom Fields & Activities)' }
    ],
    faqs: [
      {
        q: 'Kommer det signerede dokument automatisk retur på den samme Deal i Pipedrive?',
        a: 'Ja! Så snart alle parter har underskrevet i KvikSign, uploades den signerede og GlobalSign-forseglede PDF-fil automatisk tilbage under "Filer" på præcis den Deal, Kontaktperson og Organisation, den kom fra.',
        enQ: 'Is the signed document automatically returned to the same Deal in Pipedrive?',
        enA: 'Yes! As soon as all parties sign in KvikSign, the signed and GlobalSign-sealed PDF is automatically uploaded under "Files" on the exact Deal, Person, and Organization.'
      },
      {
        q: 'Hvilke værdier kan KvikSign opdatere i Pipedrive ved signering?',
        a: 'KvikSign kan automatisk sætte Deal-status til Vundet (Won) eller Tabt (Lost), flytte Dealen til et nyt stadie i din pipeline, oprette en tidslinje-note med revisionsspor samt opdatere dine valgte Custom Fields på Dealen med f.eks. underskriftsdato, MitID-navn, underskriftsmetode og transaktions-ID.',
        enQ: 'Which values can KvikSign update in Pipedrive when a document is signed?',
        enA: 'KvikSign can automatically mark the Deal as Won or Lost, move the Deal to a chosen pipeline stage, post an audit note on the timeline, and update custom Deal fields with the signing date, verified signer name, signing method, and transaction ID.'
      }
    ],
    seoTitle: 'Pipedrive Integration – Digital Signering & CRM | KvikSign',
    englishSeoTitle: 'Pipedrive Integration – Digital Signing & CRM | KvikSign',
    seoDescription: 'Integrer Pipedrive med KvikSign. Anmod om signering på en Deal, få det signerede PDF-dokument automatisk retur på Dealen og opdater værdier i Pipedrive.',
    englishSeoDescription: 'Integrate Pipedrive with KvikSign. Request signatures on Deals, automatically attach signed PDFs back to the Deal, and update Deal fields in real time.',
    iconType: 'pipedrive'
  }
];

export interface LocalizedIntegrationData {
  name: string;
  category: string;
  tagline: string;
  shortDescription: string;
  heroHeadline: string;
  heroSubheadline: string;
  heroImageAlt: string;
  badgeText: string;
  aboutTitle: string;
  aboutParagraphs: string[];
  benefitsTitle: string;
  benefits: IntegrationBenefit[];
  setupTitle: string;
  setupSteps: IntegrationStep[];
  technicalSpecs?: { label: string; value: string }[];
  faqs: { q: string; a: string }[];
  seoTitle: string;
  seoDescription: string;
}

export const SWEDISH_INTEGRATIONS_MAP: Record<string, LocalizedIntegrationData> = {
  'google-drive': {
    name: 'Google Drive',
    category: 'Ärendehantering och arkivering',
    tagline: 'Säker distribution och automatisk arkivering av viktiga filer',
    shortDescription: 'Anslut till Google Drive och låt KvikSign automatiskt spara alla signerade dokument i era valda mappar helt utan manuellt arbete.',
    heroHeadline: 'Automatisk arkivering av signerade dokument i Google Drive',
    heroSubheadline: 'Skapa integration med Google Drive och låt KvikSign arkivera alla färdigsignerade avtal med fullständigt revisionsspår i molnet. Spara tid, eliminera misstag och behåll full kontroll.',
    heroImageAlt: 'Google Drive integration för automatisk arkivering i KvikSign',
    badgeText: 'Molnlagring & Arkiv',
    aboutTitle: 'Om Google Drive-integrationen',
    aboutParagraphs: [
      'Google Drive är världens mest använda molnbaserade lagrings- och samarbetsplattform, som används av miljontals organisationer för att lagra, organisera och dela affärskritiska filer online.',
      'Tjänsten tillämpar stark kryptering både under överföring (TLS/SSL) och i vila (AES-256) i Googles säkra datacenter. Det garanterar att företagets avtal, kontrakt och personuppgifter alltid är skyddade mot obehörig åtkomst.',
      'Med KvikSigns officiella Google Drive-integration behöver du aldrig mer ladda ned en signerad PDF för att ladda upp den manuellt. Den sekund alla parter har signerat med digital signatur eller BankID, placeras den förseglade original-PDF-filen automatiskt i er önskade Google Drive-mapp.'
    ],
    benefitsTitle: 'Fördelar med en Google Drive-integration i KvikSign',
    benefits: [
      {
        title: 'Säker filöverföring & automatisk lagring',
        description: 'Alla dokument som signeras via KvikSign överförs krypterat och sparas automatiskt i Google Drive.',
        points: [
          'Direkt moln-till-moln-synkronisering utan osäkra e-postbilagor',
          'Bevarar PAdES LTV-certifikatet och det manipuleringssäkra digitala sigillet',
          'Optimerad för stora volymer av dokument och flera undertecknare'
        ]
      },
      {
        title: 'Centraliserad dokumenthantering för hela teamet',
        description: 'Samla företagets viktigaste avtal på ett ställe så att alla behöriga medarbetare har direkt åtkomst.',
        points: [
          'Sluta leta i personliga inkorgar efter signerade kontrakt',
          'Stöder både personliga diskar och delade Google Workspace-enheter (Shared Drives)',
          'Snabb textsökning bland kund- och leverantörsavtal'
        ]
      },
      {
        title: 'Styrd behörighetskontroll och GDPR-efterlevnad',
        description: 'Använd Google Drives avancerade behörighetsinställningar för att styra vem som kan läsa eller redigera känsliga filer.',
        points: [
          'Finmaskig åtkomstkontroll på användar- eller avdelningsnivå',
          'Full överensstämmelse med GDPR och dataskyddsregler',
          'Automatisk granskningslogg och versionshistorik i Google Workspace'
        ]
      },
      {
        title: 'Spara tid och eliminera manuell administration',
        description: 'Frigör värdefull arbetstid för jurister, HR-specialister, redovisare och säljteam.',
        points: [
          'Eliminerar manuella nedladdningar och filöverföringar helt',
          'Minskar risken för mänskliga arkiveringsmisstag till noll',
          'Omedelbart tillgängligt för granskning av revisorer, ledning och rådgivare'
        ]
      }
    ],
    setupTitle: 'Så här konfigurerar du Google Drive i KvikSign',
    setupSteps: [
      {
        step: 1,
        title: 'Logga in i KvikSign',
        description: 'Gå till ditt KvikSign-konto och öppna Företagsinställningar från navigationsmenyn.'
      },
      {
        step: 2,
        title: 'Välj fliken Integrationer',
        description: 'Hitta integrationsöversikten och klicka på kortet för Google Drive.'
      },
      {
        step: 3,
        title: 'Anslut ditt Google-konto',
        description: 'Ge KvikSign säker behörighet via Googles OAuth-dialog. Du behåller full kontroll över behörigheterna.'
      },
      {
        step: 4,
        title: 'Välj målmapp',
        description: 'Välj den specifika mappen i Google Drive där signerade filer automatiskt ska sparas.'
      }
    ],
    technicalSpecs: [
      { label: 'Protokoll', value: 'Google Drive REST API v3 med OAuth 2.0' },
      { label: 'Kryptering', value: 'TLS 1.3 vid överföring, AES-256 i vila' },
      { label: 'Filformat', value: 'PAdES LTV-förseglade PDF-dokument med revisionsspår' },
      { label: 'Disktyper', value: 'Min enhet samt Google Workspace delade enheter (Shared Drives)' }
    ],
    faqs: [
      {
        q: 'Skriver KvikSign över befintliga filer i Google Drive?',
        a: 'Nej. Varje signerat dokument sparas som en unik PDF-fil med dokumentets titel, transaktions-ID och tidsstämpel, så att inga tidigare filer någonsin skrivs över.'
      },
      {
        q: 'Sparas revisionsloggen och certifikaten också i Google Drive?',
        a: 'Ja. Den genererade PDF-filen är juridiskt förseglad (PAdES) och innehåller samtliga signaturer, tidsstämplar, BankID-verifieringar och den officiella granskningsloggen.'
      },
      {
        q: 'Kan vi byta mapp efter konfigureringen?',
        a: 'Ja, du kan när som helst gå in under Inställningar > Integrationer och välja en annan målmapp eller skapa en ny direkt från KvikSign.'
      }
    ],
    seoTitle: 'Google Drive Integration – Automatisk Arkivering | KvikSign',
    seoDescription: 'Arkivera automatiskt alla signerade dokument och avtal direkt i Google Drive med KvikSign. Säker, krypterad och GDPR-kompatibel molnsynkronisering.'
  },
  'slack': {
    name: 'Slack',
    category: 'Realtidsaviseringar och samarbete',
    tagline: 'Blixtsnabba uppdateringar i era Slack-kanaler när avtal signeras',
    shortDescription: 'Håll säljteamet, HR och ledningen uppdaterade i realtid. Ta emot strukturerade Slack-meddelanden med direktlänkar så fort en kund signerar.',
    heroHeadline: 'Få avisering i Slack samma sekund ett dokument signeras',
    heroSubheadline: 'Anslut KvikSign till er Slack-arbetsyta och få direkta aviseringar i dedikerade kanaler. Följ avtalens framsteg, fira nya affärer och agera omedelbart på avvisningar.',
    heroImageAlt: 'Slack integration för KvikSign-aviseringar och teamsamarbete',
    badgeText: 'Teamaviseringar & Sälj',
    aboutTitle: 'Om Slack-integrationen',
    aboutParagraphs: [
      'Slack är den ledande kommunikationshubben för moderna organisationer, där team samlas för att utbyta kunskap, koordinera projekt och fatta snabba beslut.',
      'I en hektisk vardag blir e-postaviseringar ofta fördröjda eller drunknar i överfulla inkorgar. KvikSigns Slack-integration lyfter de viktigaste affärshändelserna direkt in i den kanal där teamet redan arbetar.',
      'Oavsett om du vill ha en avisering i #sales när ett stort avtal stängs eller i #hr när ett anställningsavtal är påskrivet, levererar KvikSign tydliga Slack Block Kit-meddelanden med uppgifter om undertecknare, dokumentnamn och direktknappar till avtalet.'
    ],
    benefitsTitle: 'Fördelar med en Slack-integration i KvikSign',
    benefits: [
      {
        title: 'Snabb respons och kortare säljcykler',
        description: 'Var först med att veta när en kund har signerat så att du kan påbörja onboarding direkt.',
        points: [
          'Inga fördröjningar på grund av förbisedda e-postmeddelanden',
          'Snabb uppföljning ger ett professionellt intryck gentemot kunden',
          'Perfekt för snabbväxande sälj- och konsultorganisationer'
        ]
      },
      {
        title: 'Öppenhet och transparens för hela organisationen',
        description: 'Skapa gemensam synlighet kring viktiga avtal utan manuella statusuppdateringar eller interna möten.',
        points: [
          'Fira stängda affärer tillsammans i säljkanalen och stärk teamkänslan',
          'Ge ledning och projektledare full överblick i realtid',
          'Separata kanaler för olika avdelningar (t.ex. #sales, #hr, #management)'
        ]
      },
      {
        title: 'Interaktiva knappar och åtgärdslänkar i Slack',
        description: 'Varje meddelande kan innehålla en direkt åtgärdsknapp så att behöriga användare kan öppna dokumentet med ett klick.',
        points: [
          'Direktlänk till dokumentets status och historik i KvikSign',
          'Kompakt visning med undertecknarens namn, e-post och tidsstämpel',
          'Byggd på officiellt Slack Block Kit för optimal mobil- och skrivbordsvisning'
        ]
      },
      {
        title: 'Välj exakt vilka händelser som ska triggas',
        description: 'Anpassa aviseringarna så att ni bara får de meddelanden som är relevanta för kanalen.',
        points: [
          'Dokument slutfört (alla parter har signerat)',
          'Enskild mottagare signerade (löpande delvis framsteg)',
          'Mottagare avvisade dokument (så att ni kan agera direkt)',
          'Dokument skickat eller annullerat'
        ]
      }
    ],
    setupTitle: 'Så här ansluter du Slack i KvikSign',
    setupSteps: [
      {
        step: 1,
        title: 'Gå till Inställningar > Integrationer',
        description: 'Logga in på ditt KvikSign-konto och öppna integrationsfliken.'
      },
      {
        step: 2,
        title: 'Klicka på Anslut till Slack',
        description: 'Klicka på knappen för att starta säker auktorisering via ett popup-fönster.'
      },
      {
        step: 3,
        title: 'Välj Slack-kanal',
        description: 'I Slacks dialogruta väljer du vilken kanal (t.ex. #avtal eller #sales) som ska ta emot meddelandena.'
      },
      {
        step: 4,
        title: 'Välj händelser och testa',
        description: 'Välj vilka händelser som ska utlösa aviseringar och skicka ett snabbt testmeddelande direkt från KvikSign.'
      }
    ],
    technicalSpecs: [
      { label: 'Protokoll', value: 'Slack OAuth 2.0 Webhook API' },
      { label: 'Formatering', value: 'Slack Block Kit med interaktiva knappar' },
      { label: 'Säkerhet', value: 'OAuth 2.0 tokenbaserad behörighet' },
      { label: 'Händelser', value: 'Completed, Signed, Rejected, Cancelled, Created' }
    ],
    faqs: [
      {
        q: 'Kan vi koppla ihop flera olika Slack-kanaler?',
        a: 'Ja, du kan skapa flera Slack-integrationer i KvikSign och dirigera olika händelser till olika kanaler – t.ex. anställningar till #hr och kundkontrakt till #sales.'
      },
      {
        q: 'Är själva PDF-dokumentet synligt för alla i Slack-kanalen?',
        a: 'Nej. Av säkerhets- och GDPR-skäl skickar KvikSign en säker direktlänk till dokumentet i KvikSign, där användaren måste vara inloggad och behörig för att läsa eller hämta filen.'
      },
      {
        q: 'Fungerar integrationen både med gratis och betalda Slack-arbetsytor?',
        a: 'Ja, KvikSigns Slack-integration är fullt kompatibel med alla typer av Slack-arbetsytor (Free, Pro, Business+ och Enterprise Grid).'
      }
    ],
    seoTitle: 'Slack Integration – Realtidsaviseringar | KvikSign',
    seoDescription: 'Få direkta aviseringar i Slack när era kunder eller medarbetare signerar dokument i KvikSign. Håll sälj, HR och ledning uppdaterade i realtid.'
  },
  'sftp': {
    name: 'Säker FTP (SFTP)',
    category: 'Ärendehantering och dokumenthantering',
    tagline: 'Säker distribution av viktiga filer direkt till era egna servrar',
    shortDescription: 'Secure File Transfer Protocol gör det möjligt att överföra signerade dokument automatiskt till era egna servrar, brandväggar och lokala arkiv via krypterad SSH.',
    heroHeadline: 'Överför signerade dokument säkert till egna servrar med SFTP',
    heroSubheadline: 'Secure File Transfer Protocol gör det möjligt att skicka signerade dokument direkt till era egna servrar och interna arkiv utan tredjepartslösningar.',
    heroImageAlt: 'SFTP serverintegration för säker filöverföring i KvikSign',
    badgeText: 'Enterprise & On-Premise',
    aboutTitle: 'Om Secure File Transfer Protocol (SFTP)',
    aboutParagraphs: [
      'SFTP står för SSH File Transfer Protocol (eller Secure File Transfer Protocol). Det är den erkända industristandarden för säker överföring av filer mellan servrar via en starkt krypterad anslutning baserad på SSH-protokollet.',
      'Till skillnad från äldre protokoll som vanlig FTP krypterar SFTP både själva filinnehållet, överföringskommandona och autentiseringen (inloggningsuppgifter eller SSH-nycklar). Detta eliminerar risken för att obehöriga kan avlyssna eller manipulera data under överföringen.',
      'Med KvikSigns inbyggda SFTP-integration kan ert företag uppnå 100% automatiserad leverans av färdigsignerade dokument direkt till era egna dokumentarkiv, affärssystem (ERP) eller servermiljöer bakom er egen brandvägg.'
    ],
    benefitsTitle: 'Fördelar med en SFTP-integration i KvikSign',
    benefits: [
      {
        title: 'Säker filöverföring och fullt dataägarskap',
        description: 'Behåll full kontroll över var era signerade dokument lagras utan att vara beroende av externa publika molntjänster.',
        points: [
          'Dokument överförs direkt till era servrar via krypterad SSH',
          'Ni bestämmer själva behörigheter, mappstruktur och backuppolicyer',
          'Optimalt för hantering av stora volymer och tunga dokumentpaket'
        ]
      },
      {
        title: 'Total automatisering av dokumentflödet',
        description: 'Slipp logga in i flera system eller ladda ned filer manuellt – hela processen sker automatiskt.',
        points: [
          'Automatisk uppladdning så fort alla parter har signerat',
          'Automatisk skapande av undermappar baserat på era systemkrav',
          'Inga manuella mellansteg som kan fördröja eller förlägga filer'
        ]
      },
      {
        title: 'Högsta datasäkerhet och integritet',
        description: 'Känsliga kontrakt, personuppgifter och personnummer skyddas mot obehörig åtkomst.',
        points: [
          'Stöder stark asymmetrisk SSH-privatnyckelautentisering (samt lösenord)',
          'End-to-end-kryptering under överföringen säkerställer 100% dataintegritet',
          'Bevarar PAdES LTV-certifikat, digitala tidsstämplar och fullständig granskningslogg'
        ]
      },
      {
        title: 'Detaljerad loggning, spårbarhet och efterlevnad',
        description: 'Fullständig dokumentation över vem som har överfört vilka filer och när, i enlighet med GDPR och revisionskrav.',
        points: [
          'Inbyggt anslutningstest och automatisk felloggning i KvikSign',
          'Uppfyller de strängaste kraven inom finans, försäkring och offentlig sektor',
          'Flexibel konfiguration med anpassade sökvägar och mappstrukturer'
        ]
      }
    ],
    setupTitle: 'Så här konfigurerar du Säker FTP i KvikSign',
    setupSteps: [
      {
        step: 1,
        title: 'Gå till Inställningar > Integrationer',
        description: 'Logga in i KvikSign och välj Säker FTP (SFTP) under integrationsöversikten.'
      },
      {
        step: 2,
        title: 'Ange serveruppgifter',
        description: 'Fyll i värdnamn/IP-adress, port (standard 22) och ditt SFTP-användarnamn.'
      },
      {
        step: 3,
        title: 'Välj autentiseringsmetod',
        description: 'Välj lösenord eller klistra in en säker privat SSH-nyckel (rekommenderas för enterprise-säkerhet).'
      },
      {
        step: 4,
        title: 'Testa anslutningen och spara',
        description: 'Klicka på Testa anslutning för att verifiera anslutningen och sökvägen, och aktivera integrationen.'
      }
    ],
    technicalSpecs: [
      { label: 'Protokoll', value: 'SSH File Transfer Protocol (SFTP v3/v4)' },
      { label: 'Standardport', value: 'Port 22 (eller anpassad port)' },
      { label: 'Autentisering', value: 'Lösenord eller RSA/ED25519 privat SSH-nyckel' },
      { label: 'Mapphantering', value: 'Automatisk skapande av undermappar (auto-create)' }
    ],
    faqs: [
      {
        q: 'Vad är skillnaden mellan vanlig FTP och SFTP?',
        a: 'Vanlig FTP skickar både inloggningsuppgifter och dokument i okrypterad klartext. SFTP använder stark SSH-kryptering vilket gör överföringen helt säker mot avlyssning och manipulation.'
      },
      {
        q: 'Måste vår server ha en fast IP-adress?',
        a: 'Ja, vi rekommenderar ett fast värdnamn eller en fast offentlig IP-adress så att KvikSigns överföringstjänst alltid når er SFTP-server pålitligt.'
      },
      {
        q: 'Kan KvikSign automatiskt skapa mappar på servern?',
        a: 'Ja, KvikSign har inbyggt stöd för att automatiskt skapa den angivna sökvägen om den inte redan finns på er SFTP-server.'
      }
    ],
    seoTitle: 'SFTP Integration – Säker Filöverföring | KvikSign',
    seoDescription: 'Överför automatiskt signerade dokument till era egna servrar med KvikSigns säkra SFTP-integration. SSH-kryptering med lösenords- eller nyckelautentisering.'
  },
  'webhooks': {
    name: 'Webhooks',
    category: 'Händelsestyrd automatisering & API',
    tagline: 'Blixtsnabb realtidssynkronisering med ert CRM, ERP och verksamhetssystem',
    shortDescription: 'Koppla KvikSign direkt till er backend, ERP (t.ex. Fortnox, Visma) eller CRM (HubSpot, Salesforce). Ta emot händelsestyrda HTTP POST-anrop med strukturerade JSON-data.',
    heroHeadline: 'Automatisera era affärssystem med KvikSign Webhooks',
    heroSubheadline: 'Få direkt avisering i era egna system när ett avtal skapas, signeras, avvisas eller slutförs. Utlös automatiska fakturor, orderbekräftelser eller ärendeuppdateringar i realtid.',
    heroImageAlt: 'Webhooks API integration i KvikSign för automatisering av affärsprocesser',
    badgeText: 'REST API & Webhooks',
    aboutTitle: 'Om KvikSign Webhooks',
    aboutParagraphs: [
      'En webhook är en modern, lättviktig metod för att låta två system kommunicera i realtid utan kontinuerlig polling. När en händelse inträffar i KvikSign skickas en HTTP POST-begäran omedelbart till er specificerade endpoint.',
      'Varje webhook-anrop innehåller en komplett, strukturerad JSON-payload med transaktionens ID, dokumentnamn, status, mottagarinformation, tidsstämplar och säkra nedladdningslänkar till det signerade dokumentet.',
      'Oavsett om ni använder Zapier, Make, n8n eller ert eget egenutvecklade system i Python, Node.js, C# eller PHP kan ni bygga en robust integration på några få minuter.'
    ],
    benefitsTitle: 'Fördelar med Webhooks i KvikSign',
    benefits: [
      {
        title: 'Realtidssynkronisering utan fördröjning',
        description: 'Era system uppdateras samma sekund som en signatur sätts – inga fördröjningar, inga manuella kontroller.',
        points: [
          'Omedelbar registrering av slutförda kontrakt och kundorder',
          'Automatisk uppdatering av kundstatus i CRM (t.ex. HubSpot, Salesforce, Pipedrive)',
          'Utlös automatisk skapande av faktura i ert bokföringssystem'
        ]
      },
      {
        title: 'Fullständig och väldokumenterad JSON-payload',
        description: 'Ta emot all nödvändig information i en tydlig och överskådlig datastruktur.',
        points: [
          'Transaktions-ID, referensnummer och företagsdata',
          'Mottagarens namn, e-post och signeringsmetod (t.ex. BankID)',
          'Direkta säkra länkar för nedladdning av signerade dokument'
        ]
      },
      {
        title: 'Säker autentisering och anpassade headers',
        description: 'Skydda era webhook-endpoints mot obehöriga förfrågningar.',
        points: [
          'Stöd för Bearer Token, Basic Auth eller anpassade HTTP-headers',
          'Hemlig signeringsnyckel (Secret Token) för verifiering av avsändaren',
          'Tydlig loggning av HTTP-svarskoder (t.ex. 200 OK) för enkel felsökning'
        ]
      },
      {
        title: 'Flexibelt händelsefilter och inbyggt testverktyg',
        description: 'Välj exakt vilka händelser som ska utlösa webhooks och testa dem direkt från gränssnittet.',
        points: [
          'Filtrera på `transaction.completed`, `signer.signed`, `signer.rejected`, etc.',
          'Ett-klicks live-testfunktion som skickar en realistisk test-payload',
          'Slå på och av webhooks med ett enkelt klick'
        ]
      }
    ],
    setupTitle: 'Så här konfigurerar du Webhooks i KvikSign',
    setupSteps: [
      {
        step: 1,
        title: 'Gå till Inställningar > Integrationer',
        description: 'Logga in i KvikSign och klicka på Webhook-kortet under integrationsöversikten.'
      },
      {
        step: 2,
        title: 'Ange mottagar-URL',
        description: 'Fyll i den fullständiga HTTPS-adressen till er server, API-gateway eller automatiseringsplattform (Zapier/Make).'
      },
      {
        step: 3,
        title: 'Välj autentisering och händelser',
        description: 'Ange eventuell Bearer Token eller Basic Auth och markera de händelser som ska skickas.'
      },
      {
        step: 4,
        title: 'Skicka ett test och aktivera',
        description: 'Klicka på Skicka test för att verifiera att ert system tar emot anropet med 200 OK, och spara integrationen.'
      }
    ],
    technicalSpecs: [
      { label: 'Metod', value: 'HTTP POST med Content-Type: application/json' },
      { label: 'Autentisering', value: 'Bearer Token, Basic Auth eller Custom Header' },
      { label: 'Format', value: 'JSON Schema v4-kompatibel payload' },
      { label: 'Händelser', value: 'transaction.completed, signer.signed, signer.rejected, etc.' }
    ],
    faqs: [
      {
        q: 'Vad händer om vår server är tillfälligt nere?',
        a: 'KvikSign loggar fel och statuskoder (t.ex. 500 eller timeout). Vårt system försöker automatiskt skicka om misslyckade anrop för att säkerställa att inga data går förlorade.'
      },
      {
        q: 'Kan vi använda Webhooks tillsammans med no-code-verktyg som Zapier eller Make?',
        a: 'Ja! Du kan klistra in en webhook-URL från Zapier ("Catch Hook"), Make eller n8n direkt i KvikSign och bygga avancerade automatiserade flöden på några minuter.'
      },
      {
        q: 'Kräver Webhooks en HTTPS-anslutning?',
        a: 'Ja, av säkerhetsskäl och för att skydda affärsdata kräver KvikSign att alla webhook-adresser använder krypterad HTTPS.'
      }
    ],
    seoTitle: 'Webhooks Integration – Koppla till Affärssystem | KvikSign',
    seoDescription: 'Få realtidsuppdateringar i ert CRM, ERP och interna system med KvikSigns REST Webhooks. Automatisk synkronisering av signerade dokument via JSON.'
  }
};

export function getLocalizedIntegrationData(
  item: IntegrationDetail, 
  lang: 'da' | 'en' | 'sv' = 'da'
): LocalizedIntegrationData {
  if (lang === 'en') {
    return {
      name: item.englishName,
      category: item.englishCategory,
      tagline: item.englishTagline,
      shortDescription: item.englishShortDescription,
      heroHeadline: item.enHeroHeadline,
      heroSubheadline: item.enHeroSubheadline,
      heroImageAlt: item.englishHeroImageAlt,
      badgeText: item.englishBadgeText,
      aboutTitle: item.englishAboutTitle,
      aboutParagraphs: item.englishAboutParagraphs,
      benefitsTitle: item.englishBenefitsTitle,
      benefits: item.englishBenefits,
      setupTitle: item.englishSetupTitle,
      setupSteps: item.englishSetupSteps,
      technicalSpecs: item.technicalSpecs?.map(spec => ({
        label: spec.enLabel,
        value: spec.enValue
      })),
      faqs: item.faqs.map(faq => ({
        q: faq.enQ,
        a: faq.enA
      })),
      seoTitle: item.englishSeoTitle,
      seoDescription: item.englishSeoDescription
    };
  }

  if (lang === 'sv') {
    const sv = SWEDISH_INTEGRATIONS_MAP[item.slug];
    if (sv) {
      return sv;
    }
  }

  return {
    name: item.name,
    category: item.category,
    tagline: item.tagline,
    shortDescription: item.shortDescription,
    heroHeadline: item.heroHeadline,
    heroSubheadline: item.heroSubheadline,
    heroImageAlt: item.heroImageAlt,
    badgeText: item.badgeText,
    aboutTitle: item.aboutTitle,
    aboutParagraphs: item.aboutParagraphs,
    benefitsTitle: item.benefitsTitle,
    benefits: item.benefits,
    setupTitle: item.setupTitle,
    setupSteps: item.setupSteps,
    technicalSpecs: item.technicalSpecs?.map(spec => ({
      label: spec.label,
      value: spec.value
    })),
    faqs: item.faqs.map(faq => ({
      q: faq.q,
      a: faq.a
    })),
    seoTitle: item.seoTitle,
    seoDescription: item.seoDescription
  };
}

export function getIntegrationBySlug(slug: string): IntegrationDetail | undefined {
  const clean = slug.toLowerCase().trim();
  return INTEGRATIONS_DATA.find(
    (item) => item.slug === clean || item.englishSlug === clean || item.id === clean
  );
}
